import { getAssertion, postJson, isCapacitorNative, passkeysSupported } from '../global/webauthnUtils'

export default function PasskeyLogin({ routes, csrf, messages = {}, initialEmail = '', initialEmailFromPasskey = false }) {
  const defaults = {
    pleaseEnterEmailFirst: 'Please enter your email first.',
    signInFailedUsePassword: 'Passkey sign-in failed. Please try password login.',
    cancelledOrFailed: 'Passkey sign-in was cancelled or failed.'
  }
  const msgs = { ...defaults, ...messages }

  return {
    routes,
    csrf,
    email: '',
    initialEmailFromPasskey: false,
    isMobileApp: false,
    showPassword: false,
    dontRememberEmail: false,
    supported: false,
    loading: false,

    async init() {
      this.supported = await passkeysSupported()

      // Prefill email from server-provided cookie value (passed via Blade).
      this.email = initialEmail || ''
      this.initialEmailFromPasskey = !!initialEmailFromPasskey
      this.isMobileApp = isCapacitorNative()
      // Only hide the password form when the prefilled email came from a successful passkey login.
      // If the email comes from a failed password attempt (old('email')), keep the fields visible.
      this.showPassword = !(this.email && this.initialEmailFromPasskey)

      // Auto-trigger biometrics on mobile if email is remembered
      if (this.isMobileApp && this.initialEmailFromPasskey && this.email) {
        // Use a small timeout to ensure the UI is ready
        setTimeout(() => this.signIn(), 500)
      }
    },

    showPasswordForm() {
      // User explicitly chose password login; allow editing the email.
      this.showPassword = true
      queueMicrotask(() => document.getElementById('email')?.focus())
    },

    useAnotherEmail() {
      localStorage.removeItem('membergy_last_email')
      this.email = ''
      this.showPassword = true
      queueMicrotask(() => document.getElementById('email')?.focus())
    },

    storeEmailHint() {
      if (this.email && !this.dontRememberEmail) {
        localStorage.setItem('membergy_last_email', this.email)
      }
    },

    async signIn() {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        // Username-less attempt first if no email is present.
        const payload = this.email ? { email: this.email } : {}

        const optionsResp = await postJson(this.routes.assertOptions, payload, this.csrf)

        if (!optionsResp.ok) {
          // If we didn't provide an email and cannot proceed, reveal the email/password form.
          if (!this.email) {
            this.showPasswordForm()
            alert(msgs.pleaseEnterEmailFirst)
            return
          }
          throw new Error('Failed to get assertion options')
        }

        const serverOptions = await optionsResp.json()
        const assertion = await getAssertion(serverOptions)

        const body = {
          ...assertion,
          ...(this.email ? { email: this.email } : {}),
          dont_remember_email: this.dontRememberEmail
        }

        const loginResp = await postJson(this.routes.assert, body, this.csrf)
        if (loginResp.ok) {
          this.storeEmailHint()
          // The server decides where to go: it returns (and consumes) its own
          // intended target, e.g. the invitation the visitor came from. The
          // dashboard stays the fallback.
          let target = this.routes.redirect
          try {
            const payload = await loginResp.json()
            if (payload && typeof payload.redirect === 'string' && payload.redirect !== '') {
              target = payload.redirect
            }
          } catch (parseError) {
            console.warn('Passkey login returned no redirect target', parseError)
          }
          window.location.href = target
        } else {
          const text = await loginResp.text()
          console.error('Passkey sign-in failed', loginResp.status, text)
          // If username-less path failed, show the email/password form to try scoped passkey or password.
          if (!this.email) {
            this.showPasswordForm()
          }
          alert(msgs.signInFailedUsePassword)
        }
      } catch (e) {
        console.error(e)
        // NotAllowedError typically means user cancelled or no credential available; prompt for email in that case.
        if (e?.name === 'NotAllowedError') {
          if (!this.email) {
            this.showPasswordForm()
          }
          return
        }
        alert(msgs.cancelledOrFailed)
      } finally {
        this.loading = false
      }
    }
  }
}
